"use client";

import React, {useState} from "react";
import {useRouter} from "next/navigation";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Form, FormControl, FormField, FormItem, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Button} from "@/components/ui/button";
import {getLeadSourceValue} from "@/utils/leadSource";
import {provinces} from "@/components/FormLead";
import {CONTACT_WHATSAPP} from "@/lib/public-config";

const formSchema = z.object({
    nome: z.string().min(1, {message: "Il nome è obbligatorio."}),
    cognome: z.string().min(1, {message: "Il cognome è obbligatorio."}),
    email: z.string().email({message: "Inserisci un'email valida."}),
    telefono: z.string().min(6, {message: "Inserisci un numero di telefono valido."}),
    descrizione: z.string().optional(),
    provincia: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface LeadDialogFormProps {
    title: string;
    provincia?: string;
    mode?: "default" | "whatsapp" | "info" | "call";
    onSubmitted?: () => void;
}

export default function LeadDialogForm({title, provincia, mode = "default", onSubmitted}: LeadDialogFormProps) {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: "onTouched",
        defaultValues: {
            nome: "",
            cognome: "",
            email: "",
            telefono: "",
            descrizione: "",
            provincia: provincia ?? "",
        },
    });

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        setSubmitError(null);

        const {nome, email, cognome, telefono, provincia: provinciaCode, descrizione} = data;

        const body = {
            origine: "Richiesta appuntamento sede",
            argomento: "LDGT",
            nome: nome ?? "",
            cognome: cognome ?? "",
            telefono: telefono ?? "",
            email: email ?? "",
            provincia: provinciaCode ?? "",
            citta: provinciaCode ? provinces.find((p) => p.code === provinciaCode)?.name : "",
            motivazione: descrizione ?? null,
            fonte: getLeadSourceValue(),
            articolo: null,
            marca: null,
            modello: null,
            durata: null,
            km: null,
            anticipo: null,
        };

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/lead`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });

            const result = await response.json();
            console.log("Risultato della richiesta:", result);

            if (response.ok && result.status === "success") {
                if (mode === "whatsapp") {
                    window.open(`https://wa.me/${CONTACT_WHATSAPP}`, "_blank");
                }
                if (result.pdf) {
                    const link = document.createElement("a");
                    link.href = result.pdf;
                    link.target = "_blank";
                    link.download = "preventivo.pdf";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                router.push(
                    `/thank-you-page/noleggio?descrizione=${encodeURIComponent(title)}&email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`
                );
                form.reset();
                onSubmitted?.();
            } else {
                setSubmitError("Errore durante l'invio dei dati. Riprova più tardi.");
            }
        } catch (error) {
            console.error("Errore durante l'invio dei dati:", error);
            setSubmitError("Errore durante l'invio dei dati. Riprova più tardi.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                        control={form.control}
                        name="nome"
                        render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input placeholder="Nome" {...field} />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="cognome"
                        render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input placeholder="Cognome" {...field} />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="email"
                        render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input type="email" placeholder="Email" {...field} />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="telefono"
                        render={({field, fieldState}) => (
                            <FormItem>
                                <FormControl>
                                    <Input type="tel" placeholder="Telefono" {...field} />
                                </FormControl>
                                <FormMessage>{fieldState.error?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                </div>

                <FormField
                    control={form.control}
                    name="descrizione"
                    render={({field}) => (
                        <FormItem>
                            <FormControl>
                                <Textarea
                                    placeholder="Note (opzionale)"
                                    className="min-h-[100px] resize-none"
                                    {...field}
                                />
                            </FormControl>
                        </FormItem>
                    )}
                />

                {submitError && <p className="text-sm text-destructive">{submitError}</p>}

                <Button type="submit" className="w-full text-white" disabled={loading}>
                    {loading ? "Invio in corso..." : "Invia richiesta"}
                </Button>
            </form>
        </Form>
    );
}
